/**
 * UI-language registry.
 *
 * ## This is the *interface* language, not the learning language
 *
 * Lyder Talk is an English-learning product. Topics, questions, vocabulary
 * definitions and AI-generated study material stay in English no matter what
 * is set here — that content is the thing being learned, and translating it
 * would defeat the exercise.
 *
 * What this controls is everything *around* that content: navigation, buttons,
 * empty states, toasts, form labels, dates, relative times ("2 days ago").
 * `ui_language: "vi"` means "a Vietnamese speaker is using the app", not
 * "teach me Vietnamese".
 *
 * The one place the two axes meet is AI chat, where the assistant answers in
 * the reader's language while suggesting English material — see
 * `supabase/functions/ai-chat`.
 */

export const DEFAULT_LANGUAGE = "en" as const;

export type Language = typeof SUPPORTED_LANGUAGES[number];

export interface LanguageDef {
  /** BCP 47 tag. Also what `Intl` and `<html lang>` expect. */
  code: Language;
  /** Name in the language itself — never translated. A Vietnamese speaker
   *  looking for "Tiếng Việt" is looking for that exact string. */
  endonym: string;
  /** English name, for logs and for the `lang` attribute of subtrees. */
  name: string;
  /** Flag emoji. Decorative only — never the sole carrier of meaning. */
  flag: string;
  /** BCP 47 tag for `Intl.DateTimeFormat` / `NumberFormat`. */
  intlLocale: string;
  /** Direction. Only `ltr` today, but a `rtl` language is a one-line add. */
  dir: "ltr" | "rtl";
}

export const SUPPORTED_LANGUAGES = ["en", "vi"] as const;

export const LANGUAGES: Record<Language, LanguageDef> = {
  en: {
    code: "en",
    endonym: "English",
    name: "English",
    flag: "🇬🇧",
    intlLocale: "en-US",
    dir: "ltr",
  },
  vi: {
    code: "vi",
    endonym: "Tiếng Việt",
    name: "Vietnamese",
    flag: "🇻🇳",
    intlLocale: "vi-VN",
    dir: "ltr",
  },
};

export const LANGUAGE_LIST: LanguageDef[] = SUPPORTED_LANGUAGES.map(
  (code) => LANGUAGES[code],
);

/** Narrow an arbitrary string to a supported language code. */
export function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Resolve any BCP 47 tag to a supported language.
 *
 * Browsers hand back `vi-VN`, `vi-VN-oauth-...`, or `en-GB` depending on how
 * the tag was assembled, so prefix matching on the primary subtag is the only
 * thing that reliably works. Unknown and missing input falls back to English —
 * never to `undefined`, because an unresolvable language would leave the app
 * with no resource set at all.
 */
export function resolveLanguage(value?: string | null): Language {
  if (!value || typeof value !== "string") return DEFAULT_LANGUAGE;
  const primary = value.trim().toLowerCase().split(/[-_]/)[0];
  return isLanguage(primary) ? primary : DEFAULT_LANGUAGE;
}

export const LANGUAGE_STORAGE_KEY = "lyder-talk:ui-language:v1";